/**
 * In-memory cache of encrypted authorization tokens returned by
 * `new_callServiceAuthorizedAPI`. Mirrors the legacy
 * `UA_APP_UTILITY._UA_ENCRYPTED_TOKENS` map at firebase-things.js:8823.
 *
 * Why we need it: on the first CF call for a given service the function
 * loads TPA/SaaS credentials from Firestore, encrypts them, and returns the
 * resulting `encryptedToken` in the response envelope. If we forward that
 * token back as the `UA-Authorization-Encoded` header on subsequent calls,
 * the CF skips the Firestore credential read entirely — that's a real
 * latency + Firestore-cost win on hot paths (template fetches, message
 * sends, inbox loaders, etc.).
 *
 * Two slots (matches legacy):
 *  - `tpa`  — used when the request includes `TPAService` (carries TPA creds)
 *  - `saas` — used when no `TPAService` (carries SaaS creds)
 *
 * The map is intentionally module-level (not Zustand): tokens are
 * short-lived session credentials that must reset on full reload, never
 * persist across tabs, and don't need to trigger any UI re-render.
 *
 * Reset triggers (handled in `proxy.ts` and `functions.ts`):
 *  - 401 on a call → invalidate just that slot (creds may have rotated)
 *  - `clearProxyCache` / `clearAllCache` → wipe both slots
 *  - Impersonation / shared-admin change → caller already triggers a full
 *    page reload, so the in-memory map dies with the old window
 */

export type EncryptedTokenKey = 'tpa' | 'saas';

const encryptedTokens: Partial<Record<EncryptedTokenKey, string>> = {};

export function getEncryptedToken(key: EncryptedTokenKey): string | undefined {
  return encryptedTokens[key];
}

export function setEncryptedToken(key: EncryptedTokenKey, token: string): void {
  encryptedTokens[key] = token;
}

export function clearEncryptedToken(key: EncryptedTokenKey): void {
  delete encryptedTokens[key];
}

export function clearAllEncryptedTokens(): void {
  delete encryptedTokens.tpa;
  delete encryptedTokens.saas;
}
